import {
  Badge,
  Box,
  Flex,
  HStack,
  Input,
  Group as InputGroup,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { format } from "date-fns"
import { useState } from "react"
import {
  FiDownload,
  FiEye,
  FiFile,
  FiFileText,
  FiGlobe,
  FiImage,
  FiLock,
  FiSearch,
  FiTrash2,
} from "react-icons/fi"

import { Button } from "@/components/atoms/Button"
import useCustomToast from "@/hooks/useCustomToast"
import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import { useAuthStore } from "@/stores/auth"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@chakra-ui/react"

// Standardized async data handling
import { AsyncDataWrapper } from "@/components/hoc/withAsyncData"

interface FileInfo {
  id: string
  filename: string
  original_filename: string
  size: number // Changed from file_size
  content_type: string // Changed from file_type
  upload_type: string
  is_public: boolean
  created_at: string
  updated_at: string
  uploaded_by: {
    id: string
    email: string
    full_name?: string
  }
}

export interface FileListProps {
  uploadType?: string
  onFileSelect?: (file: FileInfo) => void
  selectable?: boolean
}

const getFileIcon = (contentType?: string) => {
  if (!contentType) return FiFile
  if (contentType.startsWith("image/")) return FiImage
  if (contentType === "application/pdf" || contentType.includes("document"))
    return FiFileText
  return FiFile
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`
}

// Separated UI component that receives data
const FileListUI = ({
  data: filesData,
  onFileSelect,
  selectable = false,
}: FileListProps & { data: unknown }) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)
  const token = useAuthStore((state) => state.accessToken)
  const queryClient = useQueryClient()
  const { showSuccessToast, showErrorToast } = useCustomToast()
  const { canReadFiles, canUpdateFiles, canDeleteFiles } = usePermissions()

  const typedData = filesData as
    | { data?: { files?: FileInfo[] } }
    | undefined
  const files = typedData?.data?.files || []

  const deleteFileMutation = useMutation({
    mutationFn: async (fileId: string) => {
      await api.files.deleteFileApiV1FilesFileIdDelete({ fileId })
    },
    onSuccess: () => {
      showSuccessToast("File deleted successfully")
      queryClient.invalidateQueries({ queryKey: ["files"] })
    },
    onError: (error: Error) => {
      showErrorToast(
        `Failed to delete file: ${error?.message || "Unknown error"}`,
      )
    },
  })

  const togglePublicMutation = useMutation({
    mutationFn: async ({
      fileId,
      isPublic,
    }: { fileId: string; isPublic: boolean }) => {
      const response =
        await api.files.setFilePublicApiV1FilesFileIdSetPublicPost({
          fileId,
          isPublic,
        })
      return response
    },
    onSuccess: (_, { isPublic }) => {
      showSuccessToast(`File is now ${isPublic ? "public" : "private"}`)
      queryClient.invalidateQueries({ queryKey: ["files"] })
    },
    onError: (error: Error) => {
      showErrorToast(
        `Failed to update file visibility: ${error?.message || "Unknown error"}`,
      )
    },
  })

  const handleDownload = async (file: FileInfo) => {
    try {
      const response = await api.files.downloadFileApiV1FilesFileIdDownloadGet({
        fileId: file.id,
      })

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response as BlobPart]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", file.original_filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      showSuccessToast("File download started")
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Unknown error"
      showErrorToast(`Failed to download file: ${message}`)
    }
  }

  const handlePreview = async (file: FileInfo) => {
    setSelectedFile(file)
    setPreviewOpen(true)

    // If it's an image, fetch it and create a blob URL
    if (file.content_type.startsWith("image/")) {
      try {
        const response = await fetch(`/api/v1/files/${file.id}/download`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          setPreviewImageUrl(url)
        }
      } catch (error) {
        console.error("Failed to load image preview:", error)
      }
    }
  }

  const handleFileClick = (file: FileInfo) => {
    if (selectable && onFileSelect) {
      onFileSelect(file)
    } else {
      handlePreview(file)
    }
  }

  const filteredFiles = files.filter(
    (file: FileInfo) =>
      file.original_filename
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      file.content_type.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  if (files.length === 0) {
    return (
      <Box maxW="full" p={0}>
        <Box borderRadius="md" borderWidth="1px" borderColor="gray.200" p={6}>
          <Stack align="center" gap={4} py={8}>
            <FiFile size={48} color="gray" />
            <Stack align="center" gap={2}>
              <Text fontSize="lg" fontWeight="medium" color="gray.600">
                No files found
              </Text>
              <Text fontSize="sm" color="gray.500">
                Upload your first file to get started
              </Text>
            </Stack>
          </Stack>
        </Box>
      </Box>
    )
  }

  return (
    <Box maxW="full" p={0}>
      <Stack gap={4}>
        {/* Search */}
        <Box>
          <InputGroup position="relative">
            <Input
              placeholder="Search files by name or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              paddingLeft="2.5rem"
            />
            <div
              style={{
                position: "absolute",
                left: "0.75rem",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                zIndex: 1,
              }}
            >
              <FiSearch />
            </div>
          </InputGroup>
        </Box>

        {/* Files Grid */}
        <Stack gap={3}>
          {filteredFiles.map((file: FileInfo) => {
            const FileIcon = getFileIcon(file.content_type)
            return (
              <Box
                key={file.id}
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
                p={4}
                cursor={selectable ? "pointer" : "default"}
                _hover={selectable ? { bg: "gray.50" } : {}}
                onClick={() => handleFileClick(file)}
              >
                <Flex align="center" gap={4}>
                  {/* File Icon and Info */}
                  <HStack flex="1" minW="0">
                    <FileIcon size={24} color="gray" />
                    <Stack gap={1} flex="1" minW="0">
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        truncate
                        title={file.original_filename}
                      >
                        {file.original_filename}
                      </Text>
                      <HStack gap={3}>
                        <Text fontSize="xs" color="gray.500">
                          {formatFileSize(file.size)}
                        </Text>
                        <Badge size="sm" variant="outline">
                          {file.content_type}
                        </Badge>
                        <Badge
                          size="sm"
                          colorScheme={file.is_public ? "green" : "gray"}
                          variant={file.is_public ? "solid" : "outline"}
                        >
                          {file.is_public ? (
                            <>
                              <FiGlobe size={10} /> Public
                            </>
                          ) : (
                            <>
                              <FiLock size={10} /> Private
                            </>
                          )}
                        </Badge>
                      </HStack>
                    </Stack>
                  </HStack>

                  {/* File Metadata */}
                  <Stack align="end" gap={1}>
                    <Text fontSize="xs" color="gray.500">
                      {format(new Date(file.created_at), "MMM dd, yyyy")}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      by {file.uploaded_by.full_name || file.uploaded_by.email}
                    </Text>
                  </Stack>

                  {/* Actions */}
                  {!selectable && (
                    <HStack>
                      {/* View/Preview action - always available if user can read files */}
                      {canReadFiles && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation()
                            handlePreview(file)
                          }}
                          title="Preview file"
                        >
                          <FiEye />
                        </Button>
                      )}

                      {/* Download action - available if user can read files */}
                      {canReadFiles && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDownload(file)
                          }}
                          title="Download file"
                        >
                          <FiDownload />
                        </Button>
                      )}

                      {/* Toggle public/private action - requires files:update scope */}
                      {canUpdateFiles && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation()
                            togglePublicMutation.mutate({
                              fileId: file.id,
                              isPublic: !file.is_public,
                            })
                          }}
                          loading={togglePublicMutation.isPending}
                          title={
                            file.is_public ? "Make private" : "Make public"
                          }
                        >
                          {file.is_public ? <FiLock /> : <FiGlobe />}
                        </Button>
                      )}

                      {/* Delete action - requires files:delete scope */}
                      {canDeleteFiles && (
                        <Button
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={(e) => {
                            e.stopPropagation()
                            if (
                              confirm(
                                `Are you sure you want to delete "${file.original_filename}"?`,
                              )
                            ) {
                              deleteFileMutation.mutate(file.id)
                            }
                          }}
                          loading={deleteFileMutation.isPending}
                          title="Delete file"
                        >
                          <FiTrash2 />
                        </Button>
                      )}
                    </HStack>
                  )}
                </Flex>
              </Box>
            )
          })}
        </Stack>

        {filteredFiles.length === 0 && searchQuery && (
          <Text textAlign="center" color="gray.500" py={8}>
            No files found matching "{searchQuery}"
          </Text>
        )}
      </Stack>

      {/* File Preview Dialog */}
      <DialogRoot
        open={previewOpen}
        onOpenChange={(e) => {
          setPreviewOpen(e.open)
          if (!e.open && previewImageUrl) {
            window.URL.revokeObjectURL(previewImageUrl)
            setPreviewImageUrl(null)
          }
        }}
        size="xl"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedFile?.original_filename}</DialogTitle>
            <DialogCloseTrigger />
          </DialogHeader>
          <DialogBody>
            {selectedFile && (
              <Stack gap={4}>
                <HStack justify="space-between">
                  <Stack gap={2}>
                    <Text fontSize="sm">
                      <strong>Size:</strong> {formatFileSize(selectedFile.size)}
                    </Text>
                    <Text fontSize="sm">
                      <strong>Type:</strong> {selectedFile.content_type}
                    </Text>
                    <Text fontSize="sm">
                      <strong>Uploaded:</strong>{" "}
                      {format(new Date(selectedFile.created_at), "PPP")}
                    </Text>
                    <Text fontSize="sm">
                      <strong>By:</strong>{" "}
                      {selectedFile.uploaded_by.full_name ||
                        selectedFile.uploaded_by.email}
                    </Text>
                  </Stack>
                  <Badge
                    colorScheme={selectedFile.is_public ? "green" : "gray"}
                    variant={selectedFile.is_public ? "solid" : "outline"}
                  >
                    {selectedFile.is_public ? "Public" : "Private"}
                  </Badge>
                </HStack>

                {/* Preview Area */}
                <Box
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={4}
                  textAlign="center"
                  minH="200px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {selectedFile.content_type.startsWith("image/") &&
                  previewImageUrl ? (
                    <Box maxW="full" maxH="400px" overflow="hidden">
                      <img
                        src={previewImageUrl}
                        alt={selectedFile.original_filename}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "400px",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  ) : selectedFile.content_type.startsWith("image/") ? (
                    <Text color="gray.500">Loading image...</Text>
                  ) : (
                    <Stack align="center" gap={3}>
                      {(() => {
                        const Icon = getFileIcon(selectedFile.content_type)
                        return <Icon size={48} color="gray" />
                      })()}
                      <Text color="gray.500">
                        Preview not available for this file type
                      </Text>
                    </Stack>
                  )}
                </Box>

                {/* Actions - scope-based visibility */}
                <HStack justify="end" gap={2}>
                  {/* Download action - available if user can read files */}
                  {canReadFiles && (
                    <Button
                      variant="outline"
                      onClick={() => handleDownload(selectedFile)}
                      gap={2}
                    >
                      <FiDownload />
                      Download
                    </Button>
                  )}

                  {/* Toggle public/private action - requires files:update scope */}
                  {canUpdateFiles && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        togglePublicMutation.mutate({
                          fileId: selectedFile.id,
                          isPublic: !selectedFile.is_public,
                        })
                      }}
                      loading={togglePublicMutation.isPending}
                    >
                      {selectedFile.is_public ? (
                        <>
                          <FiLock /> Make Private
                        </>
                      ) : (
                        <>
                          <FiGlobe /> Make Public
                        </>
                      )}
                    </Button>
                  )}

                  {/* Delete action - requires files:delete scope */}
                  {canDeleteFiles && (
                    <Button
                      colorScheme="red"
                      onClick={() => {
                        if (
                          confirm(
                            `Are you sure you want to delete "${selectedFile.original_filename}"?`,
                          )
                        ) {
                          deleteFileMutation.mutate(selectedFile.id)
                          setPreviewOpen(false)
                        }
                      }}
                      loading={deleteFileMutation.isPending}
                      gap={2}
                    >
                      <FiTrash2 />
                      Delete
                    </Button>
                  )}

                  {/* Show message when no actions are available */}
                  {!canReadFiles && !canUpdateFiles && !canDeleteFiles && (
                    <Text fontSize="sm" color="gray.500" fontStyle="italic">
                      Limited file access
                    </Text>
                  )}
                </HStack>
              </Stack>
            )}
          </DialogBody>
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}

// Main FilesListComponent using standardized async data handling (renamed to avoid browser FileList conflict)
const FilesListComponent = ({
  uploadType,
  onFileSelect,
  selectable = false,
}: FileListProps) => {
  const filesQuery = useQuery({
    queryKey: ["files", uploadType],
    queryFn: async () => {
      const response = await api.files.listFilesApiV1FilesGet({
        page: 1,
        perPage: 100,
        uploadType,
      })
      return response
    },
  })

  return (
    <AsyncDataWrapper
      query={filesQuery}
      config={{
        level: "feature",
        showEmptyState: true,
        emptyStateMessage: "No files found",
        emptyStateAction: (
          <Text fontSize="sm" color="gray.500">
            Upload your first file to get started
          </Text>
        ),
        loadingVariant: "skeleton",
        skeletonVariant: "card",
        queryOptions: {
          showErrorToast: true,
          errorMessage: "Failed to load files. Please try again.",
        },
      }}
    >
      {(data) => (
        <FileListUI
          data={data}
          uploadType={uploadType}
          onFileSelect={onFileSelect}
          selectable={selectable}
        />
      )}
    </AsyncDataWrapper>
  )
}

// Export the component with original naming for backward compatibility
export const FileList = FilesListComponent
export const FileListComponent = FilesListComponent // Keep both names available
export default FilesListComponent
